import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TranslationProvider } from '@ibk/shared-translation';
import { ReleaseType } from '@ibk/shared-types';
import { Loading } from '@ibk/shared-ui';
import { BeneficiaryValuesForm } from '../../../../components/BeneficiaryValuesForm';
import { useVoucherBeneficiaries } from '../../../../hooks';
import { beneficiosTranslations } from '../../../../translations';
import { BeneficiaryStatusInProduct } from '../../../../types';
import { convertFilterDataToSearchRequest, getUnformatedValue, hasAnyFilter, isActiveStatus, } from '../../../../utils/beneficiaryUtils';
import { DEFAULT_CURRENCY_VALUE } from '../../../../utils/maskUtils';
import styles from './ParametersStep2.module.css';
function ParametersStep2Content({ step2Data, showStep2Alert, voucher, voucherContractId, isBeneficiaryDiscountEnabled, payrollDeduction, discountType, discountValue, onReleaseTypeChange, onGlobalValueChange, onCloseStep2Alert, onUpdateBeneficiaries, onLoading, onFiltering, onEmptyStateChange, onZeroValueChange, showZeroValueError = false, }) {
    const [appliedFilters, setAppliedFilters] = useState(step2Data.appliedFilters ?? null);
    const [isFiltering, setIsFiltering] = useState(false);
    const previousFiltersRef = useRef('');
    const selectedBeneficiariesRef = useRef(step2Data.selectedBeneficiaries);
    useEffect(() => {
        selectedBeneficiariesRef.current = step2Data.selectedBeneficiaries;
    }, [step2Data.selectedBeneficiaries]);
    useEffect(() => {
        if (step2Data.appliedFilters !== undefined && step2Data.appliedFilters !== appliedFilters) {
            setAppliedFilters(step2Data.appliedFilters);
        }
    }, [step2Data.appliedFilters, appliedFilters]);
    const handleFilterChange = useCallback((filters) => {
        const hasActiveFilters = hasAnyFilter(filters, true);
        const filtersToApply = hasActiveFilters ? filters : null;
        setAppliedFilters(filtersToApply);
        onUpdateBeneficiaries(step2Data.beneficiaries, step2Data.selectedBeneficiaries, filtersToApply);
    }, [step2Data.beneficiaries, step2Data.selectedBeneficiaries, onUpdateBeneficiaries]);
    const buildSearchFilters = useCallback((filters) => {
        if (!filters) {
            return undefined;
        }
        return convertFilterDataToSearchRequest(filters, selectedBeneficiariesRef.current);
    }, []);
    const searchFilters = useMemo(() => (appliedFilters ? buildSearchFilters(appliedFilters) : undefined), [appliedFilters, buildSearchFilters]);
    const searchFiltersKey = useMemo(() => JSON.stringify(searchFilters), [searchFilters]);
    const { voucherBeneficiaries, isLoading, searchSync } = useVoucherBeneficiaries({
        voucherContractId: voucherContractId ?? '',
        voucherTypeId: voucher?.voucherType ?? '',
        searchFilters: undefined,
    });
    useEffect(() => {
        onLoading?.(isLoading);
    }, [isLoading, onLoading]);
    useEffect(() => {
        onFiltering?.(isFiltering);
    }, [isFiltering, onFiltering]);
    const calculateEmptyStateWithFilter = useCallback((filters) => {
        if (!filters) {
            return false;
        }
        const hasActiveFilters = hasAnyFilter(filters, true);
        const hasBeneficiaries = step2Data.beneficiaries.length > 0;
        return hasActiveFilters && !hasBeneficiaries && !isFiltering;
    }, [step2Data.beneficiaries.length, isFiltering]);
    const showEmptyState = useMemo(() => {
        return calculateEmptyStateWithFilter(appliedFilters);
    }, [appliedFilters, calculateEmptyStateWithFilter]);
    useEffect(() => {
        onEmptyStateChange?.(showEmptyState);
    }, [showEmptyState, onEmptyStateChange]);
    const getSelectedBeneficiariesToUse = useCallback((voucherBeneficiaries, currentSelected) => {
        if (currentSelected !== undefined) {
            return currentSelected;
        }
        return voucherBeneficiaries
            .filter(beneficiary => isActiveStatus(beneficiary.statusInProduct))
            .map(beneficiary => ({
            beneficiaryId: beneficiary.id,
            value: beneficiary.productValue,
            daysQuantity: beneficiary.productDaysQuantity ?? 0,
            discountValue: beneficiary.productDiscountValue ?? 0,
        }));
    }, []);
    const applySelectedBeneficiariesToVoucherBeneficiaries = useCallback((voucherBeneficiaries, selectedBeneficiaries) => {
        const selectedMap = new Map(selectedBeneficiaries.map(selected => [selected.beneficiaryId, selected]));
        return voucherBeneficiaries.map(beneficiary => {
            const selectedBeneficiary = selectedMap.get(beneficiary.id);
            const isSelected = selectedBeneficiary !== undefined;
            if (isSelected && selectedBeneficiary) {
                return {
                    ...beneficiary,
                    statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
                    productValue: selectedBeneficiary.value,
                    productDaysQuantity: selectedBeneficiary.daysQuantity ?? beneficiary.productDaysQuantity ?? 0,
                    productDiscountValue: selectedBeneficiary.discountValue ?? beneficiary.productDiscountValue ?? 0,
                };
            }
            return {
                ...beneficiary,
                statusInProduct: BeneficiaryStatusInProduct.INACTIVE,
            };
        });
    }, []);
    const applyGlobalValueToBeneficiaries = useCallback((beneficiaries) => {
        if (!step2Data.shouldApplyGlobalValue ||
            !step2Data.globalValue ||
            step2Data.globalValue === DEFAULT_CURRENCY_VALUE) {
            return beneficiaries;
        }
        const globalNumericValue = getUnformatedValue(step2Data.globalValue);
        const isDaily = step2Data.releaseType === ReleaseType.DAILY;
        return beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                if (isDaily) {
                    const daysQuantity = beneficiary.productDaysQuantity ?? 0;
                    const totalValue = daysQuantity > 0 ? globalNumericValue * daysQuantity : 0;
                    return {
                        ...beneficiary,
                        productValue: totalValue,
                        productDaysQuantity: daysQuantity,
                        productDiscountValue: beneficiary.productDiscountValue ?? 0,
                    };
                }
                else {
                    return {
                        ...beneficiary,
                        productValue: globalNumericValue,
                        productDaysQuantity: 0,
                        productDiscountValue: beneficiary.productDiscountValue ?? 0,
                    };
                }
            }
            return beneficiary;
        });
    }, [step2Data.shouldApplyGlobalValue, step2Data.globalValue, step2Data.releaseType]);
    const applyGlobalDiscountToBeneficiaries = useCallback((beneficiaries) => {
        if (!step2Data.shouldApplyDiscountValue || !isBeneficiaryDiscountEnabled || !payrollDeduction) {
            return beneficiaries;
        }
        const discountNumericValue = getUnformatedValue(discountValue ?? '');
        return beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                return {
                    ...beneficiary,
                    productDiscountValue: discountNumericValue,
                };
            }
            return beneficiary;
        });
    }, [step2Data.shouldApplyDiscountValue, isBeneficiaryDiscountEnabled, payrollDeduction, discountValue]);
    const updateBeneficiariesWithFilterEnabled = useCallback((newBeneficiaries, returnedSelected, useSelectedForStatus = false) => {
        const currentAppliedFilters = appliedFilters;
        let updatedBeneficiaries;
        let updatedSelectedBeneficiaries;
        const beneficiariesToProcess = applyGlobalDiscountToBeneficiaries(applyGlobalValueToBeneficiaries(newBeneficiaries));
        if (useSelectedForStatus && returnedSelected !== undefined) {
            updatedBeneficiaries = applySelectedBeneficiariesToVoucherBeneficiaries(beneficiariesToProcess, returnedSelected);
            updatedSelectedBeneficiaries = returnedSelected;
        }
        else {
            updatedBeneficiaries = beneficiariesToProcess;
            const currentSelectedMap = new Map(step2Data.selectedBeneficiaries?.map(selected => [selected.beneficiaryId, selected]) ?? []);
            updatedBeneficiaries.forEach(beneficiary => {
                if (isActiveStatus(beneficiary.statusInProduct)) {
                    currentSelectedMap.set(beneficiary.id, {
                        beneficiaryId: beneficiary.id,
                        value: beneficiary.productValue,
                        daysQuantity: beneficiary.productDaysQuantity ?? 0,
                        discountValue: beneficiary.productDiscountValue ?? 0,
                    });
                }
                else {
                    currentSelectedMap.delete(beneficiary.id);
                }
            });
            updatedSelectedBeneficiaries = Array.from(currentSelectedMap.values());
        }
        onUpdateBeneficiaries(updatedBeneficiaries, updatedSelectedBeneficiaries, currentAppliedFilters, true);
    }, [
        step2Data.selectedBeneficiaries,
        appliedFilters,
        onUpdateBeneficiaries,
        applySelectedBeneficiariesToVoucherBeneficiaries,
        applyGlobalValueToBeneficiaries,
        applyGlobalDiscountToBeneficiaries,
    ]);
    const previousVoucherBeneficiariesRef = useRef('');
    const processVoucherBeneficiariesWithFilter = useCallback((voucherBeneficiaries, useRefForSelected = false) => {
        const currentSelected = useRefForSelected ? selectedBeneficiariesRef.current : step2Data.selectedBeneficiaries;
        const selectedBeneficiariesToUse = getSelectedBeneficiariesToUse(voucherBeneficiaries, currentSelected);
        const updatedBeneficiaries = applySelectedBeneficiariesToVoucherBeneficiaries(voucherBeneficiaries, selectedBeneficiariesToUse);
        updateBeneficiariesWithFilterEnabled(updatedBeneficiaries, selectedBeneficiariesToUse);
    }, [
        step2Data.selectedBeneficiaries,
        getSelectedBeneficiariesToUse,
        applySelectedBeneficiariesToVoucherBeneficiaries,
        updateBeneficiariesWithFilterEnabled,
    ]);
    const shouldSkipProcessingVoucherBeneficiaries = useCallback(() => {
        return false;
    }, []);
    const processVoucherBeneficiariesWhenFilterEnabled = useCallback((voucherBeneficiaries, useRefForSelected = false) => {
        processVoucherBeneficiariesWithFilter(voucherBeneficiaries, useRefForSelected);
    }, [processVoucherBeneficiariesWithFilter]);
    useEffect(() => {
        if (!voucherBeneficiaries?.length || appliedFilters) {
            return;
        }
        if (shouldSkipProcessingVoucherBeneficiaries()) {
            return;
        }
        const currentBeneficiariesKey = JSON.stringify(voucherBeneficiaries.map(b => `${b.id}-${b.statusInProduct}`).sort((a, b) => a.localeCompare(b)));
        const beneficiariesChanged = previousVoucherBeneficiariesRef.current !== currentBeneficiariesKey;
        if (step2Data.beneficiaries.length === 0 || beneficiariesChanged) {
            previousVoucherBeneficiariesRef.current = currentBeneficiariesKey;
            processVoucherBeneficiariesWhenFilterEnabled(voucherBeneficiaries);
        }
    }, [
        voucherBeneficiaries,
        step2Data.beneficiaries.length,
        step2Data.beneficiaries,
        step2Data.selectedBeneficiaries,
        appliedFilters,
        shouldSkipProcessingVoucherBeneficiaries,
        processVoucherBeneficiariesWhenFilterEnabled,
    ]);
    const handleFiltersCleared = useCallback(() => {
        if (previousFiltersRef.current !== '' && voucherBeneficiaries?.length) {
            previousFiltersRef.current = '';
            processVoucherBeneficiariesWhenFilterEnabled(voucherBeneficiaries, true);
        }
    }, [voucherBeneficiaries, processVoucherBeneficiariesWhenFilterEnabled]);
    const performSearchWithFilters = useCallback(async () => {
        if (!searchFilters || !searchSync) {
            return;
        }
        setIsFiltering(true);
        previousFiltersRef.current = searchFiltersKey;
        try {
            const result = await searchSync(searchFilters);
            updateBeneficiariesWithFilterEnabled(result.beneficiaries, result.selected || [], true);
        }
        catch (error) {
            console.error('Error searching beneficiaries:', error);
        }
        finally {
            setIsFiltering(false);
        }
    }, [searchFilters, searchSync, searchFiltersKey, updateBeneficiariesWithFilterEnabled]);
    const handleFilterSearch = useCallback(() => {
        if (!searchSync) {
            return;
        }
        if (!appliedFilters) {
            handleFiltersCleared();
            return;
        }
        const filtersChanged = previousFiltersRef.current !== searchFiltersKey;
        if (!filtersChanged) {
            return;
        }
        performSearchWithFilters();
    }, [searchSync, appliedFilters, searchFiltersKey, handleFiltersCleared, performSearchWithFilters]);
    useEffect(() => {
        handleFilterSearch();
    }, [handleFilterSearch]);
    const handleApplyValue = useCallback(() => {
        const globalNumericValue = getUnformatedValue(step2Data.globalValue);
        const updatedBeneficiaries = step2Data.beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                return {
                    ...beneficiary,
                    productValue: globalNumericValue,
                };
            }
            return beneficiary;
        });
        const beneficiariesWithDiscount = applyGlobalDiscountToBeneficiaries(updatedBeneficiaries);
        updateBeneficiariesWithFilterEnabled(beneficiariesWithDiscount);
    }, [
        step2Data.globalValue,
        step2Data.beneficiaries,
        updateBeneficiariesWithFilterEnabled,
        applyGlobalDiscountToBeneficiaries,
    ]);
    const handleUpdateBeneficiariesForForm = useCallback((newBeneficiaries, returnedSelected, useSelectedForStatus = false) => {
        updateBeneficiariesWithFilterEnabled(newBeneficiaries, returnedSelected, useSelectedForStatus);
    }, [updateBeneficiariesWithFilterEnabled]);
    const hasZeroValue = useMemo(() => {
        if (step2Data.releaseType !== ReleaseType.MONTHLY)
            return false;
        const selectedBeneficiaries = step2Data.beneficiaries.filter(b => isActiveStatus(b.statusInProduct));
        return selectedBeneficiaries.length > 0 && selectedBeneficiaries.some(b => !b.productValue || b.productValue === 0);
    }, [step2Data.beneficiaries, step2Data.releaseType]);
    useEffect(() => {
        onZeroValueChange?.(hasZeroValue);
    }, [hasZeroValue, onZeroValueChange]);
    if (isLoading) {
        return (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewParametersStep2Loading', children: _jsx(Loading, {}) }));
    }
    return (_jsx(BeneficiaryValuesForm, { context: 'parameters', releaseType: step2Data.releaseType, globalValue: step2Data.globalValue, beneficiaries: step2Data.beneficiaries, showAlert: showStep2Alert, appliedFilters: appliedFilters, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, payrollDeduction: payrollDeduction, discountType: discountType, discountValue: discountValue, onReleaseTypeChange: onReleaseTypeChange, onGlobalValueChange: onGlobalValueChange, onUpdateBeneficiaries: handleUpdateBeneficiariesForForm, onApplyValue: handleApplyValue, onCloseAlert: onCloseStep2Alert, onFilterChange: handleFilterChange, isFiltering: isFiltering, hasZeroValueError: showZeroValueError }));
}
export function ParametersStep2(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(ParametersStep2Content, { ...props }) }));
}
